<template>
  <div class="app-layout">
    <!-- 移动端遮罩层 -->
    <div
      v-if="isMobile && !sidebarCollapsed"
      class="sidebar-overlay"
      @click="toggleSidebar"
    />

    <!-- 侧边栏 -->
    <aside
      class="sidebar"
      :class="{ collapsed: sidebarCollapsed }"
    >
      <div class="sidebar-header">
        <div class="logo">
          <span class="logo-icon">🦞</span>
          <span
            v-if="!sidebarCollapsed"
            class="logo-text"
          >Geekron CMS</span>
        </div>
        <button
          class="toggle-btn"
          @click="toggleSidebar"
        >
          <span v-if="sidebarCollapsed">›</span>
          <span v-else>‹</span>
        </button>
      </div>

      <nav class="sidebar-nav">
        <router-link
          to="/"
          class="nav-item"
          :class="{ active: $route.path === '/' }"
        >
          <span class="nav-icon">📊</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >仪表盘</span>
        </router-link>
        <router-link
          to="/collections"
          class="nav-item"
          :class="{ active: $route.path.startsWith('/collections') }"
        >
          <span class="nav-icon">📁</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >数据模型</span>
        </router-link>
        <router-link
          to="/users"
          class="nav-item"
          :class="{ active: $route.path === '/users' }"
        >
          <span class="nav-icon">👥</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >用户管理</span>
        </router-link>
        <router-link
          to="/files"
          class="nav-item"
          :class="{ active: $route.path === '/files' }"
        >
          <span class="nav-icon">📄</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >文件管理</span>
        </router-link>
        <router-link
          to="/api-keys"
          class="nav-item"
          :class="{ active: $route.path === '/api-keys' }"
        >
          <span class="nav-icon">🔑</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >API Keys</span>
        </router-link>
        <router-link
          to="/quotas"
          class="nav-item"
          :class="{ active: $route.path === '/quotas' }"
        >
          <span class="nav-icon">📈</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >配额管理</span>
        </router-link>
        <router-link
          to="/metadata"
          class="nav-item"
          :class="{ active: $route.path === '/metadata' }"
        >
          <span class="nav-icon">📋</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >元数据</span>
        </router-link>
        <router-link
          to="/sync"
          class="nav-item"
          :class="{ active: $route.path === '/sync' }"
        >
          <span class="nav-icon">🔄</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >数据同步</span>
        </router-link>
        <router-link
          to="/workflow"
          class="nav-item"
          :class="{ active: $route.path === '/workflow' }"
        >
          <span class="nav-icon">🔀</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >工作流管理</span>
        </router-link>
        <router-link
          to="/tenants"
          class="nav-item"
          :class="{ active: $route.path === '/tenants' }"
        >
          <span class="nav-icon">🏢</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >租户管理</span>
        </router-link>
        <router-link
          to="/settings"
          class="nav-item"
          :class="{ active: $route.path === '/settings' }"
        >
          <span class="nav-icon">⚙️</span>
          <span
            v-if="!sidebarCollapsed"
            class="nav-text"
          >设置</span>
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <div
          v-if="!sidebarCollapsed && authStore.user"
          class="user-info"
        >
          <div class="avatar">
            {{ authStore.user?.name?.charAt(0) || 'U' }}
          </div>
          <div class="user-details">
            <div class="user-name">
              {{ authStore.user?.name || '用户' }}
            </div>
            <div class="user-email">
              {{ authStore.user?.email || '' }}
            </div>
          </div>
        </div>
        <button
          class="logout-btn"
          :title="sidebarCollapsed ? '退出登录' : ''"
          @click="handleLogout"
        >
          <span class="nav-icon">🚪</span>
          <span v-if="!sidebarCollapsed">退出登录</span>
        </button>
      </div>
    </aside>

    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 顶栏 -->
      <header class="topbar">
        <!-- 移动端汉堡菜单按钮 -->
        <button
          v-if="isMobile"
          class="mobile-menu-btn"
          @click="toggleSidebar"
        >
          <span class="hamburger-icon">☰</span>
        </button>

        <div class="breadcrumb">
          <router-link
            v-for="(item, index) in breadcrumbLinks"
            :key="'link-' + index"
            :to="item.path"
            class="breadcrumb-item"
          >
            {{ item.name }}
          </router-link>
          <span
            v-for="(item, index) in breadcrumbLinks"
            :key="'sep-' + index"
            class="breadcrumb-separator"
          >/</span>
          <span
            class="breadcrumb-item active"
          >{{ lastBreadcrumbName }}</span>
        </div>
        <div class="topbar-actions">
          <span
            v-if="authStore.tenantId"
            class="tenant-badge"
          >租户：{{ authStore.tenantId.slice(0, 8) }}...</span>
        </div>
      </header>

      <!-- 内容区 -->
      <main class="content">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const sidebarCollapsed = ref(false)
const isMobile = ref(false)

// 检测屏幕尺寸
function checkMobile() {
  isMobile.value = window.innerWidth <= 768
  // 移动端默认收起侧边栏
  if (isMobile.value) {
    sidebarCollapsed.value = true
  }
}

// 监听屏幕尺寸变化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

interface BreadcrumbItem {
  name: string
  path?: string
}

const breadcrumbs = computed(() => {
  const path = route.path
  const parts = path.split('/').filter(Boolean)

  if (parts.length === 0) {
    return [{ name: '仪表盘', path: '/' }]
  }

  const result: BreadcrumbItem[] = [{ name: '仪表盘', path: '/' }]
  let currentPath = ''

  const names: Record<string, string> = {
    collections: '数据模型',
    users: '用户管理',
    settings: '设置',
  }

  parts.forEach((part, index) => {
    currentPath += '/' + part
    const name = names[part] || part

    if (index < parts.length - 1) {
      result.push({ name, path: currentPath })
    } else {
      result.push({ name, path: undefined })
    }
  })

  return result
})

const breadcrumbLinks = computed(() => {
  return breadcrumbs.value.filter((item, index) => item.path && index < breadcrumbs.value.length - 1)
})

const lastBreadcrumbName = computed(() => {
  return breadcrumbs.value[breadcrumbs.value.length - 1]?.name || ''
})

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

function handleLogout() {
  authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
/* 全局移动端优化 - 防止横向滚动 */
:global(body) {
  overflow-x: hidden;
  max-width: 100vw;
}

:global(html) {
  overflow-x: hidden;
}

.app-layout {
  display: flex;
  height: 100vh;
  background: #f5f7fa;
  overflow: hidden;
  width: 100%;
  max-width: 100vw;
}

/* 侧边栏 */
.sidebar {
  width: 240px;
  background: #1a1a2e;
  color: #fff;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  flex-shrink: 0;
}

.sidebar.collapsed {
  width: 64px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-icon {
  font-size: 1.5rem;
}

.logo-text {
  font-size: 1.125rem;
  font-weight: 600;
  white-space: nowrap;
}

.toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: background 0.2s;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.sidebar-nav {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 移动端侧边栏禁止滚动条 */
@media (max-width: 768px) {
  .sidebar {
    overflow: hidden;
  }
  
  .sidebar-nav {
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
  cursor: pointer;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.nav-item.active {
  background: #667eea;
  color: #fff;
}

.nav-icon {
  font-size: 1.25rem;
  width: 24px;
  text-align: center;
}

.nav-text {
  white-space: nowrap;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.user-details {
  flex: 1;
  overflow: hidden;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(230, 57, 70, 0.2);
  color: #e53e3e;
}

/* 主内容区 */
.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0; /* 防止 flex 子项溢出 */
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.breadcrumb-item {
  color: #666;
  cursor: pointer;
}

.breadcrumb-item:hover {
  color: #667eea;
}

.breadcrumb-item.active {
  color: #333;
  font-weight: 500;
}

.breadcrumb-separator {
  color: #ccc;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.tenant-badge {
  padding: 0.25rem 0.75rem;
  background: #e2e8f0;
  border-radius: 9999px;
  font-size: 0.75rem;
  color: #666;
}

.content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  margin-right: 0.75rem;
  border-radius: 6px;
  transition: background 0.2s;
}

.mobile-menu-btn:hover {
  background: #f0f0f0;
}

.hamburger-icon {
  font-size: 1.5rem;
  line-height: 1;
  color: #333;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .mobile-menu-btn {
    display: block;
  }

  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;
    width: 280px;
    max-width: 85vw;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.3);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    transform: translateX(-100%);
  }

  /* 移动端遮罩层 */
  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .sidebar-overlay.active {
    opacity: 1;
    visibility: visible;
  }

  /* 主内容区域适配 - 关键修复 */
  .main-container {
    width: 100vw !important;
    min-width: 0;
    flex: none !important; /* 防止被压缩 */
  }

  .topbar {
    padding: 0.75rem 1rem;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 100;
    width: 100%;
    box-sizing: border-box;
  }

  .breadcrumb {
    font-size: 0.8125rem;
    flex-wrap: wrap;
    overflow: hidden;
  }

  .content {
    padding: 1rem;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    box-sizing: border-box;
  }

  /* 确保内容区域不会被压缩 */
  .dashboard,
  .dashboard > *,
  .content > * {
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
  }
  
  /* 表格适配 */
  table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* 小屏幕手机适配 */
@media (max-width: 480px) {
  .sidebar {
    width: 260px;
    max-width: 90vw;
  }

  .topbar {
    padding: 0.5rem 0.75rem;
  }

  .breadcrumb {
    font-size: 0.75rem;
  }

  .content {
    padding: 0.75rem;
  }

  .logo-text,
  .nav-text,
  .user-details {
    display: none;
  }

  .sidebar.collapsed .logo-text,
  .sidebar.collapsed .nav-text,
  .sidebar.collapsed .user-details {
    display: none;
  }

  .sidebar-header {
    justify-content: center;
  }

  .toggle-btn {
    position: absolute;
    right: 0.5rem;
  }
}
</style>
